import { Checkbox, Image } from "antd";
import React, { useEffect, useState } from "react";

import entete from '../../../assets/img/entete_pool.png';
import PropTypes from "prop-types";
import axiosInstance from "../../../variables/api/axiosInstance";
import { frenchDate, frenchDateOnly } from "../../../variables/functions";
import { displayNumbers } from "../../../variables/numbersFunctions";
import { getCompagnieLogo } from "../../../hooks/useFilesHooks";
import { DocumentFooter } from "../../../components/docs/DocumentFooter";
import BarreTop from "../../../components/docs/BarreTop";
import TableauDossiersGroupe from "./TableauDossiersGroupe";
import SignatairesVisa from "./SignatairesVisa";
import { TYPE_DOC_SIGNATAIRE } from "../../../hooks/useSignataires";
import { cumulPaiementsSinistre } from "../../../variables/cumulReglements";
import { motifReglementAffiche } from "./groupeReglement";

const FicheDemandeReglementSinistre = React.forwardRef((props, ref) => {
    const [cumulPaye, setCumulPaye] = useState(0);
    const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
    // Signature du DG ajoutée à la demande + PERSISTÉE (clé = codeId du décaissement). Compagnies only.
    const [dgDemande, setDgDemande] = useState(false);
    const isCompany = props?.sinistre?.contrat?.organisme?.typeOrganisme !== "POOL";
    const cleDocument = props?.decaissement?.codeId;

    useEffect(() => {
        if (!isCompany || !cleDocument) return;
        axiosInstance.get(`/api/document-signature-dg/${cleDocument}`)
            .then(({ data }) => setDgDemande(!!data?.dgDemande))
            .catch(() => setDgDemande(false));
    }, [isCompany, cleDocument]);

    const toggleDgDemande = (checked) => {
        setDgDemande(checked); // maj optimiste
        axiosInstance.put(`/api/document-signature-dg/${cleDocument}`, { dgDemande: checked })
            .catch(() => setDgDemande(!checked));
    };

    const getSortDossier = (sort) => {
        const sortMap = {
            "I": "EN INSTANCE",
            "O": "OUVERT",
            "Rf": "REFUSÉ",
            "RF": "REFUSÉ",
            "T": "TERMINE",
            "S": "SANS SUITE",
            "R": "RÉOUVERT"
        };
        return sortMap[sort] || "EN INSTANCE";
    };

    useEffect(() => {
        if (props?.sinistre?.contrat?.organisme?.typeOrganisme !== "POOL") {
            getCompagnieLogo(props?.sinistre?.contrat?.organisme?.code)
                .then(async (response) => {
                    const imageBytes = await response.arrayBuffer();
                    var blob = new Blob([imageBytes], { type: "image/png" });
                    var imageUrl = URL.createObjectURL(blob);
                    setLogoPreviewUrl(imageUrl);
                })
                .catch((error) => {
                    console.log(error);
                });
        }

        // Impression : forcer le FORMAT A4 (et pas seulement portrait). Sans ça, si le navigateur
        // est réglé sur « Letter » (279 mm), le contenu de 297 mm déborde sur une 2e page (pied seul).
        const style = document.createElement('style');
        style.innerHTML = `
            @media print {
                @page {
                    size: A4 portrait;
                    margin: 0;
                }
                html, body { margin: 0 !important; padding: 0 !important; }
            }
        `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);

    // Cumul des paiements = RÈGLE CANONIQUE (cf. variables/cumulReglements) : seuls les
    // règlements AVEC chèque assigné et SANS ligne d'annulation sont comptés. On additionnait
    // auparavant TOUS les décaissements du sinistre — y compris ceux sans chèque (donc non
    // payés), les recours, les annulations et les rejets — ce qui gonflait le cumul.
    //
    // Le règlement EN COURS (celui de cette fiche) est EXCLU : il n'est pas encore payé, il
    // n'a donc pas sa place dans le cumul des paiements ANTÉRIEURS.
    useEffect(() => {
        const decaissements = props?.sinistre?.decaissements || [];
        setCumulPaye(cumulPaiementsSinistre(decaissements, {
            exclureCodeId: props?.decaissement?.codeId,
        }));
    }, [props?.sinistre?.decaissements, props?.decaissement?.codeId]);

    const sinistre = props.sinistre;
    const decaissement = props.decaissement;

    // Règlement groupé/multiple : quand plusieurs dossiers sont réglés ensemble,
    // les blocs propres à UN dossier (en-tête sinistre/police, situation
    // financière) n'ont plus de sens pour toute la fiche — ils sont masqués et
    // les infos par dossier figurent dans le tableau récapitulatif.
    const lignesGroupe = decaissement?.lignes || [];
    const isGroupe = !!decaissement?.isReglementMultiple && lignesGroupe.length > 0;
    const multiDossiers = (decaissement?.dossiers?.length || 0) > 1;

    return (
        <>
            {/* Contrôle masqué à l'impression (@media print). Compagnies uniquement. */}
            {isCompany && cleDocument && (
                <div className="dg-toolbar" style={{ maxWidth: "210mm", margin: "0 auto 10px", padding: "8px 12px", background: "#f6f7fb", borderRadius: 8, border: "1px solid #eef0f4" }}>
                    <style>{`@media print { .dg-toolbar { display: none !important; } }`}</style>
                    <Checkbox checked={dgDemande} onChange={(e) => toggleDgDemande(e.target.checked)}>
                        Ajouter la signature du <b>Directeur Général</b> (persistée à chaque réimpression)
                    </Checkbox>
                </div>
            )}
        <div ref={ref} style={{
            width: "210mm",
            // 296 mm (et non 297) = 1 mm de sécurité sous la page A4 pour éviter qu'un arrondi
            // sub-pixel (bordures, pied absolu) ne fasse déborder d'un cheveu sur une 2e page.
            minHeight: "296mm",
            margin: "0 auto",
            backgroundColor: "white",
            fontFamily: "'Comic Sans MS', 'Comic Sans', cursive, sans-serif",
            fontSize: "12px",
            boxSizing: "border-box",
            position: "relative",
            // Sticky footer : le contenu pousse le pied de page en bas (margin-top:auto), sans
            // chevaucher les signatures, sur 1 comme plusieurs pages.
            display: "flex",
            flexDirection: "column"
        }}>
            {/* Header - Full Width */}
            <div style={{
                marginBottom: "10px",
                paddingBottom: "10px"
            }}>
                <div style={{ flexShrink: 0 }}>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "0px 0px",
                        }}
                    >
                        {props?.sinistre?.contrat?.organisme?.typeOrganisme !== "POOL" && (
                            <Image
                                src={logoPreviewUrl}
                                style={{
                                    width: "100%",
                                    height: "105px",
                                }}
                            />
                        )}

                        <Image
                            src={entete}
                            style={{
                                width: "100%",
                                height: "105px",
                            }}
                        />
                    </div>
                    <BarreTop />
                </div>

            </div>

            {/* Content Area with Padding */}
            <div style={{ padding: "0 20mm" }}>

                <div style={{
                    textAlign: "center",
                    fontSize: "22px",
                    fontWeight: "bold",
                    WebkitTextStroke: "0.5px black",
                    marginBottom: "15px",
                    marginTop: "15px",
                    border: "1px solid black",
                    color: "black"
                }}>
                    FICHE DE DEMANDE DE REGLEMENT SINISTRE
                </div>
                {/* Règlement multiple : mention à la place de l'en-tête mono-dossier. */}
                {multiDossiers && (
                    <div style={{
                        textAlign: "center",
                        fontSize: "13px",
                        margin: "10px 0 20px 0",
                        color: "black"
                    }}>
                        Règlement multiple portant sur <b>{decaissement?.dossiers?.length}</b> dossiers sinistres
                        (détail dans le tableau récapitulatif ci-dessous).
                    </div>
                )}

                {/* Info Grid — un seul dossier concerné */}
                {!multiDossiers && (
                <table style={{
                    width: "100%",
                    borderCollapse: "separate",
                    borderSpacing: "4px 4px", // horizontal | vertical spacing
                    marginBottom: "20px",
                    fontSize: "12px",
                    color: "black"
                }}>
                    <tbody>
                        <tr>
                            <td style={{ padding: "2px 4px", width: "20%", border: "1px solid black" }}>Sinistre N°</td>
                            <td style={{ padding: "2px 4px", width: "30%", border: "1px solid black", textAlign: "right" }}>
                                {sinistre?.numeroSinistreM || sinistre?.numSinistre || ""}
                            </td>
                            <td style={{ width: "3px" }} /> {/* SPACE CELL */}
                            <td style={{ padding: "2px 4px", width: "20%", border: "1px solid black" }}>Police N°</td>
                            <td style={{ padding: "2px 4px", width: "30%", border: "1px solid black", textAlign: "right" }}>
                                {sinistre?.contrat?.numeroPoliceM || sinistre?.contrat?.numeroPoliceP || ""}
                            </td>
                        </tr>
                        <tr>
                            <td style={{ padding: "2px 4px", border: "1px solid black" }}>Date de survenance</td>
                            <td style={{ padding: "2px 4px", border: "1px solid black", textAlign: "right" }}>
                                {frenchDateOnly(sinistre?.dateSinistre) || ""}
                            </td>
                            <td style={{ width: "3px" }} /> {/* SPACE CELL */}
                            <td style={{ padding: "2px 4px", border: "1px solid black" }}>Effet</td>
                            <td style={{ padding: "2px 4px", border: "1px solid black", textAlign: "right" }}>
                                {frenchDateOnly(sinistre?.contrat?.dateEffet) || ""}
                            </td>
                        </tr>
                        <tr>
                            <td style={{ padding: "2px 4px", border: "1px solid black" }}>Date de Déclaration</td>
                            <td style={{ padding: "2px 4px", border: "1px solid black", textAlign: "right" }}>
                                {frenchDateOnly(sinistre?.createdAt) || ""}
                            </td>
                            <td style={{ width: "3px" }} /> {/* SPACE CELL */}
                            <td style={{ padding: "2px 4px", border: "1px solid black" }}>Echéance</td>
                            <td style={{ padding: "2px 4px", border: "1px solid black", textAlign: "right" }}>
                                {frenchDateOnly(sinistre?.contrat?.dateEcheance) || ""}
                            </td>
                        </tr>
                        <tr>
                            <td style={{ padding: "2px 4px", border: "1px solid black" }}>Branche</td>
                            <td style={{ padding: "2px 4px", border: "1px solid black", textAlign: "right" }}>
                                {/* La BRANCHE (ex. VT), pas la catégorie (ex. VL). Repli sur la catégorie
                                    si la branche n'est pas chargée. */}
                                {sinistre?.engin?.categorie?.branche?.codeBranche
                                    || sinistre?.engin?.categorie?.codeCategorie || ""}
                            </td>
                            <td style={{ width: "3px" }} /> {/* SPACE CELL */}
                            <td style={{ padding: "2px 4px", border: "1px solid black" }}>Assuré</td>
                            <td style={{ padding: "2px 4px", border: "1px solid black", textAlign: "right" }}>
                                {sinistre?.contrat?.souscripteur?.prenom
                                    ? `${sinistre?.contrat?.souscripteur?.nom} ${sinistre?.contrat?.souscripteur?.prenom}`
                                    : sinistre?.contrat?.souscripteur?.nom}
                            </td>
                        </tr>
                    </tbody>
                </table>
                )}

                {/* Section: Situation Financière — propre à UN dossier, masquée
                    pour un règlement multiple. */}
                {!multiDossiers && (
                <>
                <div style={{
                    textAlign: "center",
                    fontSize: "13px",
                    fontWeight: "normal",
                    textDecoration: "underline",
                    margin: "20px 0 15px 0",
                    color: "black"
                }}>
                    SITUATION FINANCIERE DU DOSSIER
                </div>

                <table style={{
                    width: "100%",
                    borderCollapse: "separate",
                    marginBottom: "20px",
                    fontSize: "12px",
                    color: "black"
                }}>
                    <tbody>
                        <tr>
                            <td style={{ padding: "4px 8px", width: "30%", border: "1px solid black" }}>Coût total prévisible</td>
                            <td style={{ padding: "4px 8px", width: "20%", border: "1px solid black", textAlign: "right" }}>
                                {displayNumbers(Math.trunc(sinistre?.evaluation))}
                            </td>
                            <td style={{ width: "3px" }} /> {/* SPACE CELL */}
                            <td style={{ padding: "4px 8px", width: "30%", border: "1px solid black" }}>Prévision de recours</td>
                            <td style={{ padding: "4px 8px", width: "20%", border: "1px solid black", textAlign: "right" }}>{displayNumbers(Math.trunc(sinistre?.evaluationRecours || 0))}</td>
                        </tr>
                        <tr>
                            <td style={{ padding: "4px 8px", border: "1px solid black" }}>Cumul des paiements</td>
                            <td style={{ padding: "4px 8px", border: "1px solid black", textAlign: "right" }}>
                                {displayNumbers(Math.trunc(cumulPaye))}
                            </td>
                            <td style={{ width: "3px" }} /> {/* SPACE CELL */}
                            <td style={{ padding: "4px 8px" }}></td>
                            <td style={{ padding: "4px 8px", textAlign: "right" }}></td>
                        </tr>
                        <tr>
                            <td style={{ padding: "4px 8px", border: "1px solid black" }}>SAP</td>
                            <td style={{ padding: "4px 8px", border: "1px solid black", textAlign: "right" }}>
                                {displayNumbers(Math.trunc((sinistre?.sap)))}
                            </td>
                            <td style={{ width: "3px" }} /> {/* SPACE CELL */}
                            <td style={{ padding: "4px 8px", border: "1px solid black" }}>Sort du dossier</td>
                            <td style={{ padding: "4px 8px", border: "1px solid black", textAlign: "right" }}>
                                {getSortDossier(decaissement?.sinistre?.sortDossier)}
                            </td>
                        </tr>
                    </tbody>
                </table>
                </>
                )}

                {/* Section: Demande de Règlement */}
                <div style={{
                    textAlign: "center",
                    fontSize: "13px",
                    fontWeight: "normal",
                    textDecoration: "underline",
                    margin: "20px 0 15px 0",
                    color: "black"
                }}>
                    DEMANDE DE REGLEMENT {decaissement?.dateReglement ? "DU " : ""}{frenchDate(decaissement?.dateReglement)}
                </div>

                <table style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    marginBottom: "30px",
                    fontSize: "12px",
                    color: "black"
                }}>
                    <tbody>
                        <tr>
                            <td style={{ padding: "4px 8px", width: "30%" }}>Bénéficiaire</td>
                            <td style={{ padding: "4px 8px" }}>
                                {/* Bénéficiaire au nom de l'établissement (personne morale) le cas
                                    échéant, comme sur l'ordre de dépense. */}
                                {decaissement?.beneficiaireMoral
                                    ? decaissement?.beneficiaireMoral?.nom
                                    : decaissement?.beneficiaire?.prenom
                                        ? `${decaissement?.beneficiaire?.nom} ${decaissement?.beneficiaire?.prenom}`
                                        : decaissement?.beneficiaire?.nom}
                            </td>
                        </tr>
                        <tr>
                            <td style={{ padding: "4px 8px" }}>Nature</td>
                            <td style={{ padding: "4px 8px" }}>
                                {decaissement?.lignes?.length > 1
                                    ? decaissement.lignes.map((l) => l.label).join(", ")
                                    : decaissement?.nature}
                            </td>
                        </tr>
                        <tr>
                            <td style={{ padding: "4px 8px" }}>Motif</td>
                            <td style={{ padding: "4px 8px" }}>
                                {motifReglementAffiche(decaissement)}
                            </td>
                        </tr>
                        <tr>
                            <td style={{ padding: "4px 8px" }}>Montant</td>
                            <td style={{ padding: "4px 8px" }}>
                                {displayNumbers(Math.trunc(decaissement?.montantGlobal || 0))} FCFA
                            </td>
                        </tr>
                        <tr>
                            <td style={{ padding: "4px 8px" }}>Après une retenue de</td>
                            <td style={{ padding: "4px 8px" }}>
                                {decaissement?.montantPaye
                                    ? displayNumbers(
                                        Math.trunc(
                                            (decaissement?.montantGlobal || 0) -
                                            (decaissement?.montantPaye || 0)
                                        )
                                    )
                                    : "0"} FCFA
                            </td>
                        </tr>
                    </tbody>
                </table>

                {/* Tableau récapitulatif du règlement groupé : une ligne par
                    règlement avec les infos de SON dossier (même logique que
                    l'ordre de dépense groupé). */}
                {isGroupe && (
                    <div style={{ marginBottom: "30px" }}>
                        <TableauDossiersGroupe decaissement={decaissement} sinistre={sinistre} fontSize="11px" />
                    </div>
                )}

                {/* Section: Visa et Autorisation */}
                <div style={{
                    fontSize: "13px",
                    fontWeight: "normal",
                    textDecoration: "underline",
                    marginTop: "40px",
                    marginBottom: "20px",
                    textAlign: "center",
                    color: "black"
                }}>
                    VISA ET AUTORISATION
                </div>

                {/* Signataires PARAMÉTRABLES par organisme (fallback = signataires historiques). */}
                <SignatairesVisa
                    organismeCode={sinistre?.contrat?.organisme?.code}
                    typeDocument={TYPE_DOC_SIGNATAIRE.FICHE_DEMANDE_REGLEMENT}
                    decaissementCodeId={decaissement?.codeId}
                    variant="table"
                    fallback={(isCompany
                        ? ["REDACTEUR", "CHEF SERVICE SINISTRE", "DIRECTEUR DES OPERATIONS",
                            ...(dgDemande ? ["DIRECTEUR GENERAL"] : [])]
                        : ["REDACTEUR", "DIRECTEUR DES OPERATIONS", "Directeur du Pool"]
                    ).map((t) => ({ titre: t, nom: "" }))}
                />
            </div>

            {/* Footer - Full Width. margin-top:auto → poussé en bas, après les signatures (plus de
                chevauchement sur les règlements groupés multi-pages). */}
            <div style={{
                marginTop: "auto",
                paddingBottom: "15mm",
                fontSize: "9px",
                textAlign: "center",
                color: "#666",
                borderTop: "1px solid #ccc",
                paddingTop: "10px"
            }}>
                <DocumentFooter />
            </div>
        </div>
        </>
    );
});

FicheDemandeReglementSinistre.propTypes = {
    decaissement: PropTypes.object,
    sinistre: PropTypes.object,
};

export default FicheDemandeReglementSinistre;